import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import {
  ReportsTable,
  getReportColumns,
  fmtDate,
  reportAppointmentDate,
} from "../../../../components/dashboard/service/reports/reports-table";
import { getReportsAsync } from "../../../../slices/service/reports";
import {
  reportStatusesOptions,
  reportViewsOptions,
  ReportStatus,
  FullReportAddress,
  ReportAppointmentApprovedStatus,
} from "../../../../components/dashboard/service/reports/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import {
  BuildingLibraryIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ServiceReportEditModal from "../../../../components/dashboard/service/reports/service-report-edit-modal";
import ReportMultiEditDialog from "../../../../components/dashboard/service/reports/report-multi-edit-dialog";
import { BulkBar } from "../../../../components/utils/BulkBar";
import { TableToolbar } from "../../../../components/utils/TableToolbar";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { useTableView } from "../../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../../components/utils/PMTableView/RightDrawer";
import { debounce } from "lodash";
import { ReportExportDialog } from "../../../../components/dashboard/service/reports/reports-export-dialog";
import { promangToast } from "../../../../operationFeedback";
import ReportOverviewSummary from "../../../../components/dashboard/service/reports/ReportOverviewSummary";

export const ReportList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL or default
  const getInitialFiltersFromUrl = () => {
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        const parsed = JSON.parse(filtersParam);
        // Return parsed filters even if empty (user cleared them)
        return parsed;
      } catch (e) {
        console.error("Error parsing filters from URL:", e);
      }
    }
    // Only set defaults if no filters param exists in URL at all
    return [{ name: "status", value: JSON.stringify(["NEW", "ONGOING"]) }];
  };

  const [controller, setController] = useState({
    filters: getInitialFiltersFromUrl(),
    page: searchParams.get("page") ? parseInt(searchParams.get("page")) : 1,
    per_page: searchParams.get("per_page") ? parseInt(searchParams.get("per_page")) : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "appointment",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    date_from: searchParams.get("date_from")
      ? searchParams.get("date_from")
      : "",
    date_to: searchParams.get("date_to") ? searchParams.get("date_to") : "",
    fields: searchParams.get("fields")
      ? searchParams.get("fields")
      : "id,report_number,customer.id,customer.name,customer.customer_number,project,delivery_address,status,address,address,postal,place,appointment,report_type",
  });

  const productState = useSelector((store) => {
    return store.reports;
  });

  const [reportExportDialog, setReportExportDialog] = useState(false);
  const navigate = useNavigate();

  // Table view state
  const [activeView, setActiveView] = useState("active");
  const [drawerReport, setDrawerReport] = useState(null);

  // Column definitions (with cell renderers + grouping)
  const columns = useMemo(() => getReportColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "reports",
    columns: columnMeta,
    ensureVisibleKeys: ["report_type", "appointment"],
    getSnapshot: () => ({
      filters: controller.filters,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      view: activeView,
    }),
    applySnapshot: (snap) => {
      setActiveView(snap.view || "all");
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || [],
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  // Server-side sort: toggle direction when clicking the same column
  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  // Track if we've ever had a status filter to prevent resetting to defaults
  const hasHadStatusFilterRef = useRef(false);

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  // Update all params from controller including filters
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    
    // Update standard params
    if (controller.date_from) {
      searchParams.set("date_from", controller.date_from);
    } else {
      searchParams.delete("date_from");
    }
    
    if (controller.date_to) {
      searchParams.set("date_to", controller.date_to);
    } else {
      searchParams.delete("date_to");
    }
    
    if (controller.page) {
      searchParams.set("page", controller.page);
    } else {
      searchParams.delete("page");
    }
    
    if (controller.per_page) {
      searchParams.set("per_page", controller.per_page);
    } else {
      searchParams.delete("per_page");
    }
    
    if (controller.query) {
      searchParams.set("query", controller.query);
    } else {
      searchParams.delete("query");
    }
    
    if (controller.sort) {
      searchParams.set("sort", controller.sort);
    } else {
      searchParams.delete("sort");
    }
    
    if (controller.sortBy) {
      searchParams.set("sortBy", controller.sortBy);
    } else {
      searchParams.delete("sortBy");
    }
    
    if (controller.view) {
      searchParams.set("view", controller.view);
    } else {
      searchParams.delete("view");
    }
    
    // Update filters param
    // Only set filters param if there are actual filters
    // Delete it if empty to avoid confusion with empty array string
    if (controller.filters && controller.filters.length > 0) {
      const filtersString = JSON.stringify(controller.filters);
      searchParams.set("filters", filtersString);
    } else {
      // Delete filters param when empty (user cleared all filters)
      searchParams.delete("filters");
    }
    
    // Update URL
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  }, [controller]);

  const { isLoading } = productState;
  const [selectedItems, setSelectedItems] = useState([]);

  const handleFiltersChange = (filters) => {
    const backendFilters = [];

    // Map status filters
    if (filters.statuses && filters.statuses.length > 0) {
      backendFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
      hasHadStatusFilterRef.current = true; // Mark that we've had a status filter
    } else if (filters.statuses && filters.statuses.length === 0) {
      // User explicitly cleared all status filters
      hasHadStatusFilterRef.current = true;
    }

    // Map view filters (from customDropdownStates)
    if (filters.views && filters.views.length > 0) {
      backendFilters.push({
        name: "view",
        value: JSON.stringify(filters.views),
      });
    }

    setController({
      ...controller,
      page: 1,
      query: filters.query || "",
      filters: backendFilters,
    });
  };

  // Initialize ModuleFilter state from controller (memoized)
  const initialFilters = useMemo(() => {
    const initial = {
      query: controller.query,
      statuses: [],
    };

    // Initialize views in customDropdownStates format
    let views = [];
    let hasStatusFilter = false;

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        if (filter.name === "status") {
          try {
            const parsedStatuses = JSON.parse(filter.value);
            if (parsedStatuses && parsedStatuses.length > 0) {
              initial.statuses = parsedStatuses;
              hasStatusFilter = true;
            }
          } catch (e) {
            console.error("Error parsing status filter:", e);
          }
        } else if (filter.name === "view") {
          try {
            views = JSON.parse(filter.value);
          } catch (e) {
            console.error("Error parsing view filter:", e);
          }
        }
      });
    }

    // Track if we've had a status filter before
    if (hasStatusFilter) {
      hasHadStatusFilterRef.current = true;
    }
    
    // Only set default statuses on initial mount when no filters param exists in URL at all
    // Don't set defaults if:
    // 1. filters param exists in URL (even if empty - means user cleared filters)
    // 2. controller.filters is an empty array (user explicitly cleared all filters)
    // 3. hasStatusFilter is true (status filter exists, even if empty array)
    // 4. initial.statuses already has values
    // 5. We've ever had a status filter (user has interacted with filters)
    const filtersParam = searchParams.get("filters");
    const hasFiltersParam = filtersParam !== null; // Check if param exists, not if it's truthy
    const userClearedFilters = controller.filters && controller.filters.length === 0;
    
    // Only set defaults if:
    // - No filters param in URL (first load)
    // - User hasn't explicitly cleared filters (controller.filters is not empty array)
    // - No status filter was found
    // - initial.statuses is empty
    // - We've never had a status filter before (first time user sees the page)
    if (!hasFiltersParam && !userClearedFilters && !hasStatusFilter && initial.statuses.length === 0 && !hasHadStatusFilterRef.current) {
      // Only set defaults on initial load when no filters param exists in URL at all
      // and user hasn't explicitly cleared filters and hasn't interacted with filters before
      initial.statuses = ["NEW", "ONGOING"];
    }

    // Add views to customDropdownStates format
    initial.views = views;

    return initial;
  }, [controller.query, controller.filters, searchParams]);

  const handleQueryChange = (newQuery) => {
    setController((c) => ({
      ...c,
      page: 1,
      query: newQuery,
    }));
  };

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const filterPanelPlaceholders = useMemo(
    () => [
      { key: "customer", label: t("report_list.filter.placeholder_customer") },
      { key: "project", label: t("report_list.filter.placeholder_project") },
      { key: "type", label: t("report_list.filter.placeholder_type") },
      { key: "date", label: t("report_list.filter.placeholder_date") },
    ],
    [t]
  );

  const secondaryFilterCount = useMemo(() => {
    let count = initialFilters.views?.length || 0;
    const statuses = initialFilters.statuses || [];
    if (statuses.length > 0) {
      const tabPresets = {
        active: ["NEW", "ONGOING"],
        all: [],
      };
      const preset = tabPresets[activeView] ?? [];
      const same =
        statuses.length === preset.length &&
        preset.every((s) => statuses.includes(s));
      if (!same) count += 1;
    }
    return count;
  }, [initialFilters, activeView]);

  const hasSecondaryFilters = secondaryFilterCount > 0;

  // Infinite scroll: load the next page and append results
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(getReportsAsync({ ...controller, page: nextPage, append: true }));
    setController((c) => ({ ...c, page: nextPage }));
  };

  const debouncedDispatchAction = debounce((controller) => {
    dispatch(getReportsAsync(controller));
  }, 1000);

  useEffect(() => {
    // Skip when handleLoadMore already dispatched for this controller update
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  // Convert status options to ModuleFilter format
  const statusOptions = reportStatusesOptions.map((option) => ({
    value: option.value,
    label: option.label,
    class: "bg-blue-100 text-blue-800",
  }));

  // Convert view options to ModuleFilter format
  const viewOptions = reportViewsOptions.map((option) => ({
    value: option.value,
    label: option.label,
    class: "bg-purple-100 text-purple-800",
  }));

  const headerActions = [
    {
      component: null,
      label: "Export Excel",
      description: "",
      path: null,
      action: () => setReportExportDialog(true),
      icon: BuildingLibraryIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  const [reportEditDialogOpen, setReportEditDialogOpen] = useState(false);
  const [multiEditDialogOpen, setMultiEditDialogOpen] = useState(false);
  const [reportOverviewDialogOpen, setReportOverviewDialogOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  // View tabs — clicking a tab applies a filter preset
  const reportViews = [
    { id: "active", name: t("Active") },
    { id: "all", name: t("All") },
  ];

  const handleViewChange = (viewId) => {
    setActiveView(viewId);
    if (viewId === "active") {
      setController((c) => ({
        ...c,
        page: 1,
        filters: [{ name: "status", value: JSON.stringify(["NEW", "ONGOING"]) }],
      }));
    } else if (viewId === "all") {
      setController((c) => ({ ...c, page: 1, filters: [] }));
    }
  };

  const listCount = productState.count != null ? `${productState.count}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        title={t("Reports")}
        listCount={listCount}
        actions={headerActions}
        mainButton={{
          label: t("New"),
          action: () => setReportEditDialogOpen(true),
          path: null,
          icon: PlusIcon,
        }}
      >
        <div className="p-3 space-y-2 md:p-5 md:space-y-4">
          {/* Mobile: compact search + collapsed filters */}
          <div className="space-y-2 md:hidden">
            <div className="flex items-center gap-2">
              <div className="relative flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200">
                <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <input
                  type="text"
                  value={controller.query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  placeholder={t("report_list.search_placeholder")}
                  className="min-w-0 flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
                {controller.query ? (
                  <button
                    type="button"
                    onClick={() => handleQueryChange("")}
                    className="flex-shrink-0 text-slate-400 hover:text-slate-600"
                    aria-label={t("Clear")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className={`relative inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors focus:outline-none ${
                  hasSecondaryFilters
                    ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                }`}
                aria-label={t("report_list.filter_title")}
              >
                <FunnelIcon className="h-4 w-4" />
                {hasSecondaryFilters ? (
                  <span className="min-w-[18px] rounded-full bg-white/20 px-1.5 text-center text-[11px] tabular-nums">
                    {secondaryFilterCount}
                  </span>
                ) : null}
              </button>
            </div>

            {!mobileFiltersOpen && hasSecondaryFilters ? (
              <ModuleFilter
                chipsOnly
                disabled={isLoading}
                onFiltersChange={handleFiltersChange}
                initialFilters={initialFilters}
                acceptedStatuses={reportStatusesOptions.map((opt) => opt.value)}
                customDropdownFilters={[
                  {
                    key: "views",
                    label: "Views",
                    options: viewOptions,
                    icon: BuildingLibraryIcon,
                  },
                ]}
              />
            ) : null}
          </div>

          {/* Desktop: full filter card */}
          <div className="hidden md:block">
            <ModuleFilter
              filterCard
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={initialFilters}
              searchPlaceholder={t("report_list.search_placeholder")}
              filterTitle={t("report_list.filter_title")}
              acceptedStatuses={reportStatusesOptions.map((opt) => opt.value)}
              customDropdownFilters={[
                {
                  key: "views",
                  label: "Views",
                  options: viewOptions,
                  icon: BuildingLibraryIcon,
                },
              ]}
            />
          </div>

          {/* Table card */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)] md:rounded-[18px]">
            <TableToolbar
              views={reportViews}
              activeView={activeView}
              onViewChange={handleViewChange}
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <ReportsTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              onItemsSelect={(items) => setSelectedItems(items)}
              itemState={productState}
              density={tableView.density}
              hasMore={(productState.count || 0) > (productState.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onRowClick={(report) => setDrawerReport(report)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Floating bulk action bar */}
      <BulkBar
        count={selectedItems.length}
        onClear={() => setSelectedItems([])}
        actions={[
          {
            label: t("Edit"),
            icon: PencilSquareIcon,
            action: () => setMultiEditDialogOpen(true),
            disabled: selectedItems.length === 0,
          },
          { separator: true },
          {
            label: t("Delete"),
            icon: TrashIcon,
            danger: true,
            action: () => promangToast.error("Kommer snart"),
          },
        ]}
      />

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerReport}
        onClose={() => setDrawerReport(null)}
        eyebrow={drawerReport ? `${t("Report")} #${drawerReport.report_number}` : ""}
        title={
          drawerReport?.customer
            ? `${drawerReport.customer.customer_number || ""} ${drawerReport.customer.name || ""}`.trim()
            : t("Report")
        }
        actions={[
          {
            label: t("Overview"),
            onClick: () => {
              setSelectedReportId(drawerReport.id);
              setReportOverviewDialogOpen(true);
              setDrawerReport(null);
            },
          },
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/service/reports/${drawerReport.id}`),
          },
        ]}
      >
        {drawerReport && (
          <>
            <DrawerSection title={t("Report")}>
              <DrawerProps
                items={[
                  { label: t("Status"), value: <ReportStatus report={drawerReport} /> },
                  {
                    label: t("Type"),
                    value: drawerReport.report_type?.title || "—",
                  },
                  {
                    label: t("Customer"),
                    value: drawerReport.customer
                      ? `${drawerReport.customer.customer_number || ""} ${drawerReport.customer.name || ""}`.trim() || "—"
                      : "—",
                  },
                  {
                    label: t("project"),
                    value:
                      drawerReport.project?.displayTitle ||
                      drawerReport.project?.name ||
                      "—",
                  },
                  {
                    label: t("Address"),
                    value: FullReportAddress(drawerReport) || "—",
                  },
                  {
                    label: t("Created"),
                    value: fmtDate(drawerReport.created_at),
                  },
                ]}
              />
            </DrawerSection>

            <DrawerSection title={t("Appointment")}>
              <DrawerProps
                items={[
                  {
                    label: t("Date"),
                    value: fmtDate(reportAppointmentDate(drawerReport)),
                  },
                  {
                    label: t("Status"),
                    value: drawerReport.appointment ? (
                      <ReportAppointmentApprovedStatus report={drawerReport} />
                    ) : (
                      t("Ikke avtalt")
                    ),
                  },
                  {
                    label: t("Confirmed"),
                    value: drawerReport.appointment?.confirmed_via_text
                      ? t("Via SMS")
                      : drawerReport.appointment?.confirmed_manually
                      ? t("Manually")
                      : "—",
                  },
                  {
                    label: t("Confirmed date"),
                    value: drawerReport.appointment?.confirmed_date
                      ? fmtDate(drawerReport.appointment.confirmed_date)
                      : "—",
                  },
                ]}
              />
            </DrawerSection>
          </>
        )}
      </RightDrawer>

      <ReportMultiEditDialog
        selectedItems={selectedItems}
        open={multiEditDialogOpen}
        onClose={() => setMultiEditDialogOpen(false)}
        onRefresh={() => dispatch(getReportsAsync(controller))}
      />

      <ServiceReportEditModal
        open={reportEditDialogOpen}
        onClose={() => setReportEditDialogOpen(false)}
        selected={null}
      />

      <ReportExportDialog
        open={reportExportDialog}
        isLoading={isLoading}
        controller={controller}
        onClose={() => setReportExportDialog(false)}
      />

      <ReportOverviewSummary
        open={reportOverviewDialogOpen}
        onClose={() => setReportOverviewDialogOpen(false)}
        reportId={selectedReportId}
        currentUser={null}
      />

      <PMDialog
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title={t("report_list.filter_title")}
        size="md"
        dense
        closeOnOutsideClick
      >
        <div className="overflow-y-auto overscroll-contain px-3 pb-3 sm:px-4 sm:pb-4">
          <ModuleFilter
            panelMode
            hideSearch
            disabled={isLoading}
            onFiltersChange={handleFiltersChange}
            initialFilters={initialFilters}
            acceptedStatuses={reportStatusesOptions.map((opt) => opt.value)}
            customDropdownFilters={[
              {
                key: "views",
                label: "Views",
                options: viewOptions,
                icon: BuildingLibraryIcon,
              },
            ]}
            panelPlaceholderSlots={filterPanelPlaceholders}
          />
        </div>
      </PMDialog>
    </>
  );
};
